import DatePickerWrapper from '../../components/DatePicker/DatePickerWrapper';
import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, Chip, IconButton, MenuItem, Select, Skeleton, Tooltip, Typography } from '@mui/material';
import { getAllUsersForTasks } from "../../services/UserService";
import Edit from '@mui/icons-material/Edit';
import Add from '@mui/icons-material/Add';
import WarningAmber from '@mui/icons-material/WarningAmber';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import moment from "moment";
import "moment/locale/fr";
import { getUserData } from "../../utils/FunctionHelpers";
import { withSnackbar } from "../../utils/SnackbarHOC";
import EditorCustom from "../EditorCustorm/EditorCustom";
import { removeLineBreak } from "../../utils/EditorUtil";
import LoadingButtonCustom from "../LoadingButtonCustom/LoadingButtonCustom";
import Modal from "../Modal/Modal";
import { AppButton } from "../UI";

// The API sends the status as a boolean, but some callers pass a label.
const toDone = (value) => value === true || value === 'true' || /termin|fait/i.test(String(value ?? ''));

const initials = (user) => `${user?.prenom?.[0] ?? ''}${user?.nom?.[0] ?? ''}`.toUpperCase() || '?';
const fullName = (user) => `${user?.prenom ?? ''} ${(user?.nom ?? '').toUpperCase()}`.trim();

const at = (daysFromNow, hour) => moment().add(daysFromNow, 'days').hour(hour).minute(0).second(0).toDate();
const quickDates = [
  { label: "Aujourd'hui 18h", get: () => at(0, 18) },
  { label: 'Demain 9h', get: () => at(1, 9) },
  { label: 'Dans 3 jours', get: () => at(3, 9) },
  { label: 'Dans 1 semaine', get: () => at(7, 9) },
];

const FieldLabel = ({ children, required, error }) => (
  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: error ? '#dc2626' : '#334155', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.75 }}>
    {children} {required && <Box component="span" sx={{ color: '#dc2626' }}>*</Box>}
  </Typography>
);

const ErrorText = ({ children }) => children ? (
  <Typography sx={{ fontSize: '0.78rem', color: '#dc2626', mt: 0.5 }}>{children}</Typography>
) : null;

const StatusOption = ({ selected, done, onClick }) => {
  const color = done ? { main: '#166534', bg: '#f0fdf4', border: '#86efac' } : { main: '#854d0e', bg: '#fef9c3', border: '#fde047' };
  return (
    <Box
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      sx={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1.1, borderRadius: '10px', cursor: 'pointer',
        fontWeight: 600, fontSize: '0.92rem', userSelect: 'none', transition: 'all 0.15s ease',
        color: selected ? color.main : '#64748b',
        background: selected ? color.bg : '#fff',
        border: `1.5px solid ${selected ? color.border : '#e2e8f0'}`,
        '&:hover': { borderColor: color.border },
        '&:focus-visible': { outline: '2px solid #aed6f1', outlineOffset: 2 },
      }}
    >
      {done ? <CheckCircleOutlined sx={{ fontSize: 19 }} /> : <RadioButtonUnchecked sx={{ fontSize: 19 }} />}
      {done ? 'Fait' : 'À faire'}
    </Box>
  );
};

const FormTache = ({
  reloadingData,
  setReloadingData,
  oldAffecteA,
  oldStatut,
  oldCommentaire,
  oldDateTime,
  editMode,
  tacheId,
  snackbarShowMessage,
  editTache,
  addTache,
  id,
}) => {
  const [modalState, setModalState] = useState({ open: false, ecole: "" });
  const [affecteA, setAffecteA] = useState(oldAffecteA ? oldAffecteA : getUserData().uuid);
  const [statut, setStatut] = useState(toDone(oldStatut));
  const [commentaire, setCommentaire] = useState(oldCommentaire !== "" ? oldCommentaire : "");
  const [dateTime, setDateTime] = useState(oldDateTime ? oldDateTime : new Date());
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const loaded = useRef(true);
  const me = getUserData()?.uuid;

  useEffect(() => {
    if (!editMode) {
      setCommentaire("");
    }
  }, [editMode]);

  const validate = () => {
    let temp = {};
    temp.affecteA = affecteA ? "" : "Choisissez la personne à qui affecter la tâche.";
    const isCommentaireValid = typeof commentaire === "string" && commentaire.replace(/<\/?[^>]+(>|$)/g, "").trim() !== "";
    temp.commentaire = isCommentaireValid ? "" : "Le commentaire est obligatoire.";
    temp.dateTime = dateTime ? "" : "La date est obligatoire.";
    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    const isValid = validate();
    setIsFormValid(isValid);
  }, [affecteA, commentaire, dateTime]);

  // Show errors once the user starts editing (the save button is disabled while the form is invalid).
  const editField = (setter) => (value) => { setTouched(true); setter(value); };

  const handleOpen = () => {
    // Start from the saved values each time, so a cancelled edit doesn't leak into the next one.
    setAffecteA(oldAffecteA ? oldAffecteA : me);
    setStatut(toDone(oldStatut));
    setDateTime(oldDateTime ? oldDateTime : new Date());
    setCommentaire(oldCommentaire);
    setTouched(false);
    setModalState({ open: true });
  };

  React.useEffect(() => {
    let active = true;
    if (modalState.open) {
      (async () => {
        setLoadingUsers(true);
        try {
          const users = await getAllUsersForTasks();
          if (!active) return;
          setUsers(users.data);
          setLoadingUsers(false);
        } catch (error) {
          if (!active) return;
          snackbarShowMessage("Erreur de chargement des utilisateurs.", "error", 4000);
          setLoadingUsers(false);
        }
      })();
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.open]);

  const handleClose = () => {
    if (loading) return;
    setModalState({ open: false });
    setCommentaire("");
  };

  const handleSubmit = () => {
    setTouched(true);
    if (validate()) {
      (async () => {
        setLoading(true);
        const tache = {
          dateTime: dateTime,
          affectePar: getUserData().uuid,
          affecteA: affecteA,
          statut: statut,
          commentaire: removeLineBreak(commentaire),
          contact: id,
        };
        try {
          snackbarShowMessage("Patientez svp!", "warning", null);
          await addTache(id, tache);
          snackbarShowMessage("Tâche bien ajoutée!");
          setReloadingData(!reloadingData);
        } catch (e) {
          snackbarShowMessage("Une erreur est survenue.", "error", 4000);
        } finally {
          setLoading(false);
          handleClose();
        }
      })();
    }
  };

  const handleUpdate = () => {
    setTouched(true);
    if (validate()) {
      (async () => {
        setLoading(true);
        const tachePayload = {
          affecteA: affecteA,
          statut: statut,
          commentaire: removeLineBreak(commentaire),
          dateTime: dateTime,
        };
        try {
          snackbarShowMessage("Patientez svp!", "warning", null);
          await editTache(tachePayload, tacheId);
          snackbarShowMessage("La tâche a été mise à jour !");
          if (loaded.current) {
            setReloadingData(!reloadingData);
            setLoading(false);
            handleClose();
          }
        } catch (e) {
          if (loaded.current) {
            snackbarShowMessage("une erreur est survenue.", "error", 4000);
            setLoading(false);
            handleClose();
          }
        }
      })();
    }
  };

  const editorCustomProps = {
    value: commentaire,
    setValue: editField(setCommentaire),
    handleSubmit,
    enableOnBlur: false,
  };

  const loadingButtonProps = {
    handleSubmit: handleSubmit,
    handleUpdate: handleUpdate,
    loading,
    editMode: editMode,
    disabled: !isFormValid,
  };

  const sortedUsers = [...users].sort((a, b) => fullName(a).localeCompare(fullName(b), 'fr'));
  const showError = (field) => touched && errors[field];
  const selectedDay = dateTime ? moment(dateTime) : null;
  const isPast = selectedDay && selectedDay.isBefore(moment(), 'minute') && !statut;

  const body = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <FieldLabel required error={showError('affecteA')}>Affectée à</FieldLabel>
          {me && affecteA !== me && !loadingUsers && (
            <Chip
              icon={<PersonOutlined sx={{ fontSize: 16 }} />}
              label="M'affecter"
              size="small"
              onClick={() => editField(setAffecteA)(me)}
              sx={{ mb: 0.75, height: 24, fontWeight: 600, color: '#03407b', background: '#ebf4fb', border: '1px solid #aed6f1', '&:hover': { background: '#dbeefa' } }}
            />
          )}
        </Box>
        {loadingUsers ? (
          <Skeleton variant="rounded" height={44} sx={{ borderRadius: '10px' }} />
        ) : (
          <Select
            fullWidth
            displayEmpty
            value={sortedUsers.some((u) => u.uuid === affecteA) ? affecteA : ''}
            onChange={(e) => editField(setAffecteA)(e.target.value)}
            error={!!showError('affecteA')}
            MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
            renderValue={(value) => {
              const user = sortedUsers.find((u) => u.uuid === value);
              if (!user) return <Typography sx={{ color: '#94a3b8' }}>Choisir une personne…</Typography>;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Avatar sx={{ width: 26, height: 26, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#ebf4fb', color: '#03407b' }}>{initials(user)}</Avatar>
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#0f172a' }}>{fullName(user)}</Typography>
                  {user.uuid === me && <Chip label="Moi" size="small" sx={{ height: 20, fontSize: '0.7rem', background: '#f1f5f9' }} />}
                </Box>
              );
            }}
            sx={{ borderRadius: '10px', '& .MuiSelect-select': { py: 1.1 } }}
          >
            {sortedUsers.map((user) => (
              <MenuItem value={user.uuid} key={user.uuid} sx={{ gap: 1.25 }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#ebf4fb', color: '#03407b' }}>{initials(user)}</Avatar>
                {fullName(user)}
                {user.uuid === me && <Chip label="Moi" size="small" sx={{ height: 20, fontSize: '0.7rem', ml: 'auto', background: '#f1f5f9' }} />}
              </MenuItem>
            ))}
          </Select>
        )}
        <ErrorText>{showError('affecteA')}</ErrorText>
      </Box>

      <Box>
        <FieldLabel required>Statut</FieldLabel>
        <Box role="radiogroup" sx={{ display: 'flex', gap: 1.25 }}>
          <StatusOption done={false} selected={!statut} onClick={() => setStatut(false)} />
          <StatusOption done selected={statut} onClick={() => setStatut(true)} />
        </Box>
      </Box>

      <Box>
        <FieldLabel required error={showError('commentaire')}>Commentaire</FieldLabel>
        <Box sx={{
          borderRadius: '10px', overflow: 'hidden',
          border: `1px solid ${showError('commentaire') ? '#fca5a5' : 'transparent'}`,
        }}>
          <EditorCustom id="note" {...editorCustomProps} />
        </Box>
        <ErrorText>{showError('commentaire')}</ErrorText>
      </Box>

      <Box>
        <FieldLabel required error={showError('dateTime')}>Date / heure</FieldLabel>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
          {quickDates.map((q) => (
            <Chip
              key={q.label}
              label={q.label}
              size="small"
              onClick={() => editField(setDateTime)(q.get())}
              sx={{ height: 26, fontWeight: 600, fontSize: '0.78rem', color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', '&:hover': { background: '#ebf4fb', color: '#03407b', borderColor: '#aed6f1' } }}
            />
          ))}
        </Box>
        <DatePickerWrapper
          style={{ marginTop: "0px" }}
          invalidDateMessage=""
          format="dd/MM/yyyy HH:mm"
          inputVariant="outlined"
          value={dateTime}
          fullWidth
          error={!!showError('dateTime')}
          onChange={editField(setDateTime)}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />
        {selectedDay?.isValid() && (
          <Typography sx={{ fontSize: '0.78rem', mt: 0.75, color: isPast ? '#b91c1c' : '#64748b' }}>
            {isPast && <><WarningAmber sx={{ fontSize: 15, verticalAlign: 'text-bottom', mr: 0.5 }} />Cette date est déjà passée — </>}
            {selectedDay.locale('fr').format('dddd D MMMM YYYY [à] HH:mm')} ({selectedDay.locale('fr').fromNow()})
          </Typography>
        )}
        <ErrorText>{showError('dateTime')}</ErrorText>
      </Box>
    </Box>
  );

  const actions = (
    <>
      <AppButton variant="secondary" onClick={handleClose} disabled={loading}>
        Annuler
      </AppButton>
      <LoadingButtonCustom {...loadingButtonProps} />
    </>
  );

  return (
    <>
      {editMode ? (
        <Tooltip title="Modifier">
          <IconButton sx={{ color: '#C8D148' }} onClick={handleOpen}>
            <Edit />
          </IconButton>
        </Tooltip>
      ) : (
        <AppButton
          variant="primary"
          size="small"
          onClick={handleOpen}
          startIcon={<Add fontSize="small" />}
          sx={{ mb: 1.5 }}
        >
          Ajouter une tâche
        </AppButton>
      )}

      <Modal
        open={!!modalState.open}
        onClose={handleClose}
        title={editMode ? "Modifier une tâche" : "Ajouter une tâche"}
        maxWidth="sm"
        actions={actions}
      >
        {body}
      </Modal>
    </>
  );
};

export default withSnackbar(FormTache);
